/**
 * GlobalClickTracker Component
 * 
 * React wrapper for global click tracking.
 * Manages lifecycle and integrates with consent and auth contexts.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAnalyticsConsent } from '@/hooks/useAnalyticsConsent';
import { getSessionId } from '@/lib/analytics/deviceInfo';
import { 
  initGlobalClickTracking, 
  cleanupClickTracking,
  updateClickTrackingOptions 
} from '@/lib/analytics/globalClickTracking';

export function GlobalClickTracker() {
  const { user } = useAuth();
  const { canTrack, loading, userObjectsToAnalytics } = useAnalyticsConsent();
  const isInitializedRef = useRef(false);

  // GDPR Art. 21: an active objection short-circuits BOTH tiers
  // (mem://compliance/gdpr-objection-enforcement).
  const trackingAllowed = !userObjectsToAnalytics;

  // Initialize tracking on mount
  useEffect(() => {
    // Wait for consent check to complete
    if (loading) return;

    // Get session ID only if we have consent AND no active objection
    const sessionId = canTrack && trackingAllowed ? getSessionId() : undefined;

    initGlobalClickTracking({
      canTrack: canTrack && trackingAllowed,
      userId: user?.id,
      sessionId,
    });

    isInitializedRef.current = true;

    return () => {
      cleanupClickTracking();
      isInitializedRef.current = false;
    };
  }, [loading]); // Only reinit on loading change

  // Update options when consent or user changes
  useEffect(() => {
    if (!isInitializedRef.current || loading) return;

    const sessionId = canTrack && trackingAllowed ? getSessionId() : undefined;

    updateClickTrackingOptions({
      canTrack: canTrack && trackingAllowed,
      userId: user?.id,
      sessionId,
    });
  }, [canTrack, user?.id, loading, trackingAllowed]);
  
  // This component doesn't render anything
  return null;
}

export default GlobalClickTracker;
